import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useAuth } from '../context/AuthContext';
import { cancelSubscription } from '../services/subscription-service';

interface CancelSubscriptionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCancel: () => void;
    onError: (message: string) => void;
}

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
                                                                             isOpen,
                                                                             onClose,
                                                                             onCancel,
                                                                             onError,
                                                                         }) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);

    const handleConfirmCancel = async () => {
        if (!user?.id) {
            onError('There was a problem while attempting to cancel. Please try logging in again.');
            onClose();
            return;
        }
        setLoading(true);
        try {
            const response = await cancelSubscription(user.id);
            if (response.status === 200) {
                onCancel();
            } else {
                onError('Unable to cancel at this time. Please try again later or contact support if the issue persists.');
            }
        } catch {
            onError('Unable to cancel at this time. Please try again later or contact support if the issue persists.');
        } finally {
            setLoading(false);
            onClose();
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            className="fixed inset-0 z-50 flex items-center justify-center"
        >
            <div
                className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm"
                aria-hidden="true"
                onClick={onClose}
            />
            <div className="relative bg-white w-full max-w-md p-6 rounded-lg shadow-lg">
                <Dialog.Title className="text-xl font-semibold mb-4 text-red-600">
                    Cancel Subscription
                </Dialog.Title>
                <p className="mb-2">
                    Are you sure you want to cancel your subscription?
                </p>
                <p className="mb-4 text-sm text-gray-600">
                    Your current subscription will remain active until the end of your billing period.
                </p>
                <div className="flex justify-end space-x-3">
                    <button
                        data-testid="close-btn"
                        onClick={onClose}
                        className="px-4 py-2 rounded border"
                        disabled={loading}
                    >
                        Keep subscription
                    </button>
                    <button
                        data-testid="confirm-cancel-btn"
                        onClick={handleConfirmCancel}
                        className="px-4 py-2 rounded bg-red-600 text-white disabled:opacity-50"
                        disabled={loading}
                    >
                        {loading ? 'Cancelling...' : 'Yes, cancel'}
                    </button>
                </div>
            </div>
        </Dialog>
    );
};

export default CancelSubscriptionModal;